import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Search,
  X,
  ChevronRight,
  ArrowUp,
  LayoutDashboard,
  Users,
  Briefcase,
  GraduationCap,
  Award,
  ShieldAlert,
  Building,
  MessageSquare,
  Send,
  Bot,
  Layers,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  FileText,
  Filter,
  BarChart2,
  Lock,
  Sparkles,
  Zap,
  PenLine,
  Table2,
  ChartLine,
  Lightbulb,
  Star,
  TriangleAlert
} from 'lucide-react';
import { getAvailableModulesForRole, searchUserManual, generateDynamicFallbackModule } from '../utils/manualEngine';
import './UserManual.css';

// Map icon string names to Lucide Icon components
const ICON_MAP = {
  LayoutDashboard,
  Users,
  Briefcase,
  GraduationCap,
  Award,
  ShieldAlert,
  Building,
  MessageSquare,
  Send,
  Bot,
  Layers,
  BookOpen,
  HelpCircle
};

const UserManualPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeModuleKey, setActiveModuleKey] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  const userRole = (localStorage.getItem('role') || 'guest').toLowerCase();

  // Dynamically compute modules accessible for current role
  const roleModules = useMemo(() => {
    const modules = getAvailableModulesForRole(userRole);

    // If current route is not in standard registry, generate a dynamic fallback entry
    if (location.pathname && location.pathname !== '/user-manual' && !modules.some(m => m.route.startsWith(location.pathname))) {
      const fallback = generateDynamicFallbackModule(location.pathname, userRole);
      return [fallback, ...modules];
    }

    return modules;
  }, [userRole, location.pathname]);

  // Filter modules based on search query
  const filteredModules = useMemo(() => {
    return searchUserManual(roleModules, searchQuery);
  }, [roleModules, searchQuery]);

  // Set initial active module
  useEffect(() => {
    if (filteredModules.length > 0 && !activeModuleKey) {
      setActiveModuleKey(filteredModules[0].key);
    }
  }, [filteredModules, activeModuleKey]);

  // Scroll spy & Back-to-Top scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToModule = (key) => {
    setActiveModuleKey(key);
    const element = document.getElementById(`module-section-${key}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Group modules by Category for Sidebar ToC
  const groupedSidebarModules = useMemo(() => {
    const groups = {};
    filteredModules.forEach((mod) => {
      const cat = mod.category || 'General Features';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(mod);
    });
    return groups;
  }, [filteredModules]);

  return (
    <div className="user-manual-page">
      {/* HEADER BANNER */}
      <div className="manual-header-banner">
        <div className="manual-header-content">
          <div className="manual-title-row">
            <div>
              <div className="manual-title-badge">
                <Sparkles size={14} /> Live System Documentation
              </div>
              <h1 className="manual-main-title">Platform User Manual</h1>
              <p className="manual-subtitle">
                Dynamic guide for <strong>{userRole.toUpperCase()}</strong> role. Automatically updated across routes, permissions, and features.
              </p>
            </div>
            <button className="nav-btn-outline" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
              ← Return to Dashboard
            </button>
          </div>

          {/* SEARCH BAR */}
          <div className="manual-search-wrapper">
            <Search className="manual-search-icon" size={20} />
            <input
              type="text"
              className="manual-search-input"
              placeholder="Search features, actions, buttons, forms, reports, permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="manual-search-clear" onClick={() => setSearchQuery('')}>
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div className="manual-container">
        {/* SIDEBAR TABLE OF CONTENTS */}
        <aside className="manual-sidebar">
          <div className="sidebar-title">Table of Contents</div>

          {Object.keys(groupedSidebarModules).length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No matching modules.</p>
          ) : (
            Object.entries(groupedSidebarModules).map(([category, items]) => (
              <div key={category}>
                <div className="sidebar-category-label">{category}</div>
                {items.map((mod) => {
                  const IconComp = ICON_MAP[mod.icon] || Layers;
                  const isActive = activeModuleKey === mod.key;
                  return (
                    <button
                      key={mod.key}
                      className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                      onClick={() => scrollToModule(mod.key)}
                    >
                      <IconComp size={16} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {mod.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </aside>

        {/* MAIN DOCUMENTATION CONTENT */}
        <main className="manual-main-content">
          {filteredModules.length === 0 ? (
            <div className="manual-empty-state">
              <AlertCircle size={48} style={{ color: 'var(--cyan)', marginBottom: '12px' }} />
              <div className="empty-title">No Matching Documentation Found</div>
              <div className="empty-desc">
                No features matched your search term "{searchQuery}". Try searching for actions like "Create", "Reports", "LMS", or "Permissions".
              </div>
            </div>
          ) : (
            filteredModules.map((mod) => {
              const IconComp = ICON_MAP[mod.icon] || Layers;
              return (
                <section key={mod.key} id={`module-section-${mod.key}`} className="manual-module-card">
                  {/* BREADCRUMBS */}
                  <div className="manual-breadcrumbs">
                    <span>User Manual</span>
                    <ChevronRight size={14} />
                    <span>{mod.category}</span>
                    <ChevronRight size={14} />
                    <span className="active">{mod.title}</span>
                  </div>

                  {/* MODULE HEADER */}
                  <div className="module-header" style={{ marginTop: '16px' }}>
                    <div className="module-title-box">
                      <div className="module-icon-container">
                        <IconComp size={24} />
                      </div>
                      <div>
                        <h2 className="module-title">{mod.title}</h2>
                      </div>
                    </div>
                  </div>

                  {/* 1. PAGE OVERVIEW & PURPOSE */}
                  <div className="doc-sub-section">
                    <div className="doc-section-heading">
                      <BookOpen size={18} /> Page Overview & Purpose
                    </div>
                    <p className="doc-text-paragraph">{mod.overview}</p>
                    <p className="doc-text-paragraph">
                      <strong>Operational Purpose:</strong> {mod.purpose}
                    </p>
                  </div>

                  {/* 2. AVAILABLE ACTIONS */}
                  {mod.actions && mod.actions.length > 0 && (
                    <div className="doc-sub-section">
                      <div className="doc-section-heading">
                        <CheckCircle size={18} /> Available Actions & Buttons
                      </div>
                      <div className="actions-grid">
                        {mod.actions.map((act, idx) => (
                          <div key={idx} className="action-pill-card">
                            <div className="action-name"><Zap className="manual-inline-icon" size={15} aria-hidden="true" /> {act.name}</div>
                            <div className="action-desc">{act.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 3. FORMS & INPUT FIELDS */}
                  {mod.forms && mod.forms.length > 0 && (
                    <div className="doc-sub-section">
                      <div className="doc-section-heading">
                        <FileText size={18} /> Form Inputs & Controls
                      </div>
                      <div className="actions-grid">
                        {mod.forms.map((f, idx) => (
                          <div key={idx} className="action-pill-card">
                            <div className="action-name"><PenLine className="manual-inline-icon" size={15} aria-hidden="true" /> {f.field}</div>
                            <div className="action-desc">
                              <strong>Type:</strong> {f.type} — {f.description}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 4. TABLES & DATA GRIDS */}
                  {mod.tables && mod.tables.length > 0 && (
                    <div className="doc-sub-section">
                      <div className="doc-section-heading">
                        <Filter size={18} /> Tables & Data Grids
                      </div>
                      {mod.tables.map((tbl, idx) => (
                        <div key={idx} className="manual-table-spec">
                          <div className="table-spec-name"><Table2 className="manual-inline-icon" size={17} aria-hidden="true" /> {tbl.name}</div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '6px' }}>
                            Columns Included:
                          </div>
                          <div className="columns-tag-list">
                            {tbl.columns.map((col, cIdx) => (
                              <span key={cIdx} className="column-tag">
                                {col}
                              </span>
                            ))}
                          </div>
                          {tbl.features && (
                            <div style={{ fontSize: '0.8rem', color: 'var(--cyan)', marginTop: '8px' }}>
                              Supported Controls: {tbl.features.join(' • ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 5. CHARTS & ANALYTICS */}
                  {mod.charts && mod.charts.length > 0 && (
                    <div className="doc-sub-section">
                      <div className="doc-section-heading">
                        <BarChart2 size={18} /> Charts & Analytical Metrics
                      </div>
                      {mod.charts.map((ch, idx) => (
                        <div key={idx} className="manual-table-spec">
                          <div className="table-spec-name"><ChartLine className="manual-inline-icon" size={17} aria-hidden="true" /> {ch.name}</div>
                          <p className="doc-text-paragraph" style={{ fontSize: '0.85rem', margin: '4px 0' }}>
                            <strong>Tracked Metrics:</strong> {ch.metrics.join(', ')}
                          </p>
                          <p className="doc-text-paragraph" style={{ fontSize: '0.85rem', margin: '4px 0' }}>
                            <strong>Calculation Logic:</strong> {ch.calculation}
                          </p>
                          <p className="doc-text-paragraph" style={{ fontSize: '0.85rem', margin: '4px 0' }}>
                            <strong>Interpretation Guide:</strong> {ch.interpretation}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 6. WORKFLOW DIAGRAM */}
                  {mod.workflow && mod.workflow.length > 0 && (
                    <div className="doc-sub-section">
                      <div className="doc-section-heading">
                        <Layers size={18} /> Step-by-Step Workflow
                      </div>
                      <div className="workflow-step-flow">
                        {mod.workflow.map((step, idx) => (
                          <React.Fragment key={idx}>
                            <div className="workflow-step-node">
                              {idx + 1}. {step}
                            </div>
                            {idx < mod.workflow.length - 1 && <span className="workflow-arrow">→</span>}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 7. PERMISSIONS & DEPENDENCIES */}
                  {mod.permissions && (
                    <div className="doc-sub-section">
                      <div className="doc-section-heading">
                        <Lock size={18} /> Security & Role Access Controls
                      </div>
                      <p className="doc-text-paragraph" style={{ fontSize: '0.88rem' }}>
                        <strong>Authorized Roles:</strong> {mod.permissions.allowed.join(', ').toUpperCase()}
                        <br />
                        <strong>Access Dependencies:</strong> {mod.permissions.dependencies}
                      </p>
                    </div>
                  )}

                  {/* 8. TIPS & BEST PRACTICES */}
                  {mod.tips && (
                    <div className="callout-box tip">
                      <Lightbulb className="manual-inline-icon" size={17} aria-hidden="true" /> <strong>Pro Tip:</strong> {mod.tips}
                    </div>
                  )}
                  {mod.bestPractices && (
                    <div className="callout-box best-practice">
                      <Star className="manual-inline-icon" size={17} aria-hidden="true" /> <strong>Best Practice:</strong> {mod.bestPractices}
                    </div>
                  )}

                  {/* 9. COMMON ERRORS */}
                  {mod.commonErrors && mod.commonErrors.length > 0 && (
                    <div className="doc-sub-section" style={{ marginTop: '16px' }}>
                      <div className="doc-section-heading" style={{ color: '#ef4444' }}>
                        <AlertCircle size={18} /> Troubleshooting & Common Errors
                      </div>
                      {mod.commonErrors.map((err, idx) => (
                        <div key={idx} className="faq-item-accordion" style={{ borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                          <div className="faq-question" style={{ color: '#f87171' }}>
                            <TriangleAlert className="manual-inline-icon" size={17} aria-hidden="true" /> {err.error}
                          </div>
                          <div className="faq-answer">{err.resolution}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 10. FAQS */}
                  {mod.faqs && mod.faqs.length > 0 && (
                    <div className="doc-sub-section" style={{ marginTop: '16px' }}>
                      <div className="doc-section-heading">
                        <HelpCircle size={18} /> Frequently Asked Questions (FAQs)
                      </div>
                      {mod.faqs.map((faq, idx) => (
                        <div key={idx} className="faq-item-accordion">
                          <div className="faq-question">Q: {faq.q}</div>
                          <div className="faq-answer">A: {faq.a}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </main>
      </div>

      {/* BACK TO TOP BUTTON */}
      {showBackToTop && (
        <button className="back-to-top-btn" onClick={scrollToTop} title="Back to Top">
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default UserManualPage;
